import { Box } from '@mui/material'
import { useLocation } from 'react-router-dom'

interface UniversalDataGridLayoutTypes {
    cookieCrumbs?: React.ReactNode
    toolbar?: React.ReactNode
    dataGrid: React.ReactNode
    tabBar?: React.ReactNode
}

export const UniversalDataGridLayout = ({
    cookieCrumbs,
    toolbar,
    tabBar,
    dataGrid,
}: UniversalDataGridLayoutTypes) => {
    // Admin sections render the (compact) heading inline-left of the section
    // tabs to save vertical space; everywhere else keeps the stacked title row.
    const { pathname } = useLocation()
    const compact = pathname.includes('/admin')
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                // Clear the fixed AppBar.
                marginTop: 9,
                minWidth: 0,
                width: '100%',
            }}
        >
            {compact ? (
                (cookieCrumbs || tabBar) && (
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 2.5,
                            px: 3,
                            minHeight: 48,
                            borderBottom: 1,
                            borderColor: 'divider',
                            flexWrap: 'wrap',
                        }}
                    >
                        {cookieCrumbs}
                        {tabBar && (
                            <Box sx={{ flex: 1, minWidth: 0 }}>{tabBar}</Box>
                        )}
                    </Box>
                )
            ) : (
                <>
                    {cookieCrumbs && (
                        <Box
                            sx={{
                                px: 3,
                                py: 2,
                                borderBottom: 1,
                                borderColor: 'divider',
                            }}
                        >
                            {cookieCrumbs}
                        </Box>
                    )}
                    {tabBar ? <Box>{tabBar}</Box> : null}
                </>
            )}

            {toolbar && <Box sx={{ p: 2 }}>{toolbar}</Box>}

            {/* minWidth:0 lets a wide DataGrid scroll internally instead of
                pushing the page into horizontal overflow. */}
            <Box
                sx={{
                    flexGrow: 1,
                    minWidth: 0,
                    width: '100%',
                }}
            >
                {dataGrid}
            </Box>
        </Box>
    )
}

interface PageContainerTypes {
    children: React.ReactNode
    /** Max content width on ultra-wide screens. */
    maxWidth?: number | string
    /** Skip the top offset that clears the fixed AppBar. */
    disableTopOffset?: boolean
}

/**
 * Standard page shell: uniform gutters, a max width so content doesn't sprawl on
 * ultra-wide monitors, and top spacing that clears the fixed AppBar. minWidth:0
 * prevents flex children from causing horizontal overflow.
 */
export const PageContainer = ({
    children,
    maxWidth = 1680,
    disableTopOffset = false,
}: PageContainerTypes) => (
    <Box
        sx={{
            pt: disableTopOffset ? 0 : { xs: 8, md: 9 },
            px: { xs: 2, md: 3 },
            pb: 4,
            mx: 'auto',
            width: '100%',
            maxWidth,
            minWidth: 0,
        }}
    >
        {children}
    </Box>
)

const UniversalLayouts = {
    UniversalDataGridLayout,
    PageContainer,
}

export default UniversalLayouts
